import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import DownloadIcon from "@atlaskit/icon/core/download";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import ShowMoreVerticalIcon from "@atlaskit/icon/core/show-more-vertical";
import {token} from "@atlaskit/tokens";
import {createFileRoute, useNavigate, useSearch} from "@tanstack/react-router";
import {useCallback, useEffect, useMemo, useRef, useState, type Ref} from "react";
import {z} from "zod";
import {CopyDataDropdownItem} from "~/components/dataActions/CopyDataDropdownItem";
import {EmotoradLeadDispositionModal} from "~/components/emotorad/emotoradLeadDispositionModal";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {
    createOttopilotListView,
    useEmotoradCustomersListCardPresentation,
} from "~/components/ottopilotDataViewer/emotoradListCardPresentation";
import {useListNotesPanel} from "~/components/notes/useListNotesPanel";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {SortDirection} from "~/components/ottopilotDataViewer/sortConfiguration";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotExportActions} from "~/components/ottopilotDataViewer/useOttopilotExportActions";

import {WorkspaceListCardCreateChrome} from "~/components/pageScaffolds/workspacePageScaffold/bottomNav/workspaceListCardCreateChrome";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import type {OttopilotCustomer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotCustomersWorkspaceCapability/domain/OttopilotCustomer";
import type {OttopilotCustomersWorkspaceCapabilityConfiguration as CustomersModuleConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotCustomersWorkspaceCapability/domain/OttopilotCustomersWorkspaceCapabilityConfiguration";
import {OttopilotEventStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import {
    useOptionalWorkspaceCapabilityConfiguration,
    useRequiredWorkspaceMobileNavigationConfiguration,
} from "~/hooks/workspaceConfiguration";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import {getCustomers, getCustomers_ColumnValues, getCustomers_Length, type EventContainerWithEvents} from "~/utilities/apis/api";
import {
    EmotoradEventTypes,
    EMOTORAD__CUSTOMERS__ASSIGNED_DEALERS_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_NAME_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_PLATFORM_COLUMN_ID,
    EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID,
    EMOTORAD__CUSTOMERS__PHONE_NUMBER_COLUMN_ID,
    getEmotoradListEventCreateItems,
    getEmotoradListModuleConfig,
    getEmotoradJourneyScopeFilterStatusIdsForEvents,
} from "~/domainModels/workspaceSpecific/emotorad";
import {resolveWorkspaceListCreateItemsConfiguration} from "~/domainModels/listView/resolveWorkspaceListCreateItemsConfiguration";
import {
    resolveCalendarAllDayStyle,
    resolveCalendarEventTypeColor,
    resolveCalendarEventTypeLabel,
    resolveCalendarLegendItems,
} from "~/utilities/sharedCalendarConfig";
import {
    formatDateTime,
    transformRowForExport,
} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {uniqueUuid} from "@vesta/unique/unique";
import {CustomerEntityProfileAside} from "~/components/entityProfile/customerEntityProfileAside";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {CUSTOMER_PROFILE_FROM} from "~/utilities/customerProfileNavigation";
import {CustomerViewPanel} from "./-customer-view-panel";
import {CustomerKanbanBoardCard} from "./-customer-kanban-board";
import {formatEmotoradCustomerPlatformCell} from "./-format-emotorad-customer-platform";
import {customerTableViewColumnsToOttopilotColumns} from "./-table-view";
import {withKanbanAccentTextColors} from "~/components/ottopilotDataViewer/listStatusConfig";
import {
    filterKanbanColumnsForModuleStatusId,
    getEmotoradCustomersStatusNavConfig,
    getEmotoradModuleStatusNavValidStatusIds,
    resolveEmotoradCustomerStatusValuesForModuleNavStatusId,
} from "~/domainModels/moduleStatusNav";
import {useSyncModuleStatusFilterFromSearch} from "~/hooks/useSyncModuleStatusFilterFromSearch";
import {resolveCustomersKanbanConfiguration, resolveCustomersKanbanFilterFieldIdForQuery} from "./-kanban-view";
import {createCustomersTableColumnResolvers} from "./-table-resolver";
import {resolveCustomersViewPanelConfiguration} from "./-view-panel";
import {useOnlyEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import type {BtlActivity} from "~/domainModels/btlActivities";

const customersSearchSchema = z.object({
    view: z.enum(["list", "table", "kanban", "calendar"]).optional().default("table").catch("table"),
    statusId: z.string().optional(),
});

const CUSTOMERS_LIST_VIEW_ID = uniqueUuid`e8f3c7d9-24d7-4fd9-9ca9-fdc1fc30b0ba`;
const CUSTOMERS_TABLE_VIEW_ID = uniqueUuid`c4d5e6f7-a8b9-4c0d-9e1f-2a3b4c5d6e7f`;
const CUSTOMERS_KANBAN_VIEW_ID = uniqueUuid`d5e6f7a8-b9c0-4d1e-9f2a-3b4c5d6e7f8a`;
const CUSTOMERS_CALENDAR_VIEW_ID = uniqueUuid`e6f7a8b9-c0d1-4e2f-9a3b-4c5d6e7f8a9b`;

function normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
}

function isEmotoradCustomersWorkspace(customersModuleConfiguration: CustomersModuleConfiguration): boolean {
    return (
        customersModuleConfiguration.additionalColumns.some(
            (c) => c.id === EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID,
        ) === true
    );
}

function normalizeForMatch(value: string): string {
    return value.trim().toLowerCase().replaceAll(/\s+/g, " ");
}

function matchesAnyLabel(displayName: string, labels: ReadonlyArray<string>): boolean {
    const normalized = normalizeForMatch(displayName);
    return labels.some((label) => {
        const target = normalizeForMatch(label);
        return normalized === target || normalized.includes(target);
    });
}

function isEmotoradCustomerAssignedDealersColumn(candidate: {id: string; displayName: string}): boolean {
    if (candidate.id === EMOTORAD__CUSTOMERS__ASSIGNED_DEALERS_COLUMN_ID) {
        return true;
    }
    const normalized = normalizeForMatch(candidate.displayName);
    return normalized === "assigned dealers" || normalized === "assigned dealer";
}

function unknownToDisplayString(raw: unknown): string {
    if (raw === undefined || raw === null) {
        return "";
    }
    if (typeof raw === "string") {
        return raw;
    }
    if (typeof raw === "number" || typeof raw === "boolean") {
        return String(raw);
    }
    return JSON.stringify(raw);
}

type CustomerDataViewerRow = OttopilotCustomer & {
    title?: string;
    startDate?: string;
    endDate?: string;
    isAlldayEvent?: boolean;
};

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/customers/",
)({
    validateSearch: (search: Record<string, unknown>) => customersSearchSchema.parse(search),
    component: RouteComponent,
});

function RouteComponent() {
    const customersModuleConfiguration = useOptionalWorkspaceCapabilityConfiguration(WorkspaceCapabilityType.ottopilotCustomers);
    if (customersModuleConfiguration === null) {
        return (
            <TableOnlyPage
                title="customers"
                bottomSection={
                    <div className="p-ds-space-200">
                        The customers module is not available for this workspace.
                    </div>
                }
            />
        );
    }
    return <CustomersRouteLoaded customersModuleConfiguration={customersModuleConfiguration} />;
}

function CustomersRouteLoaded({
    customersModuleConfiguration,
}: {
    customersModuleConfiguration: CustomersModuleConfiguration;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const search = useSearch({
        from: "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/customers/",
    });
    const navigate = useNavigate();
    const {workspace} = useWorkspace();
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const eventContainerTemplate = useOnlyEventContainerTemplate();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const eventTemplates = useEventTemplates();
    const products = useProductCatalogue();
    const {showFlag} = useAnankeFlags();
    const isMobile = useCheckMobile();
    const {openNotesPanel, listNotesPanelChrome} = useListNotesPanel();
    const [customerListCreateDrawerOpen, setCustomerListCreateDrawerOpen] = useState(false);
    const [customerLeadDispositionCustomer, setCustomerLeadDispositionCustomer] =
        useState<CustomerDataViewerRow | null>(null);
    const baseWorkspaceMobileNavigationConfiguration = useRequiredWorkspaceMobileNavigationConfiguration();
    const workspaceMobileNavigationConfiguration = useMemo(
        () =>
            resolveWorkspaceListCreateItemsConfiguration({
                workspace,
                configuration: baseWorkspaceMobileNavigationConfiguration,
            }),
        [workspace, baseWorkspaceMobileNavigationConfiguration],
    );
    const emotoradCustomersListConfig = getEmotoradListModuleConfig("customers");
    const listEventCreateDrawerItems = useMemo(() => {
        if (emotoradCustomersListConfig.includeEventCreateDrawer !== true) {
            return [] as const;
        }
        return getEmotoradListEventCreateItems(workspaceMobileNavigationConfiguration);
    }, [workspaceMobileNavigationConfiguration, emotoradCustomersListConfig.includeEventCreateDrawer]);
    const customerPageConfig = customersModuleConfiguration.customerPageConfig;

    const resolvedKanbanConfiguration = useMemo(() => {
        const resolved = resolveCustomersKanbanConfiguration(customerPageConfig, eventContainerTemplate);
        if (isEmotoradCustomersWorkspace(customersModuleConfiguration) !== true) {
            return resolved;
        }
        return {
            ...resolved,
            columnConfig: withKanbanAccentTextColors(resolved.columnConfig),
        };
    }, [customerPageConfig, eventContainerTemplate, customersModuleConfiguration]);

    const isEmotoradCustomers = isEmotoradCustomersWorkspace(customersModuleConfiguration);

    // Emotorad list cards use `resolvedKanbanConfiguration.columnConfig`; kanban must use the same
    // columns and filter the customer status column (where LIVE / churned UUIDs are stored).
    const customersKanbanColumnConfig = resolvedKanbanConfiguration.columnConfig;

    const customersKanbanColumnConfigForStatus = useMemo(
        () => filterKanbanColumnsForModuleStatusId(customersKanbanColumnConfig, search.statusId),
        [customersKanbanColumnConfig, search.statusId],
    );

    const customersKanbanFilterFieldId = useMemo(() => {
        const filterFieldId =
            isEmotoradCustomers === true
                ? EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID
                : resolvedKanbanConfiguration.filterFieldId;
        return resolveCustomersKanbanFilterFieldIdForQuery(
            {...resolvedKanbanConfiguration, filterFieldId},
            customerPageConfig.tableViewColumns,
        );
    }, [
        isEmotoradCustomers,
        resolvedKanbanConfiguration,
        customerPageConfig.tableViewColumns,
    ]);

    const customersJourneyScopeFilterConfig = useMemo(() => {
        // "Display by" control is implemented as the Journey scope filter.
        // Enable it only for Emotorad Customers, and only when journeys module declares a status column.
        if (eventContainerTemplate.configuration.statusColumnConfig === undefined) {
            return undefined;
        }
        if (isEmotoradCustomersWorkspace(customersModuleConfiguration) !== true) {
            return undefined;
        }
        const groups = getEmotoradJourneyScopeFilterStatusIdsForEvents(workspace);
        if (groups === null) {
            return undefined;
        }
        return {
            statusColumnId: eventContainerTemplate.configuration.statusColumnConfig.columnId,
            activeStatusIds: groups.activeStatusIds,
            inactiveStatusIds: groups.inactiveStatusIds,
        };
    }, [customersModuleConfiguration, eventContainerTemplate.configuration.statusColumnConfig, workspace]);

    const resolvedViewPanel = useMemo(() => {
        return resolveCustomersViewPanelConfiguration(getAdditionalColumns, customerPageConfig, customersModuleConfiguration, eventContainerTemplate);
    }, [getAdditionalColumns, customerPageConfig, customersModuleConfiguration, eventContainerTemplate]);

    const tableResolvers = useMemo(() => {
        return createCustomersTableColumnResolvers(customerPageConfig.tableViewColumns);
    }, [customerPageConfig.tableViewColumns]);

    const calendarConfig = customerPageConfig.calendarConfig;
    const hasCalendarConfig = calendarConfig !== undefined;

    const getCustomerCalendarType = useMemo(() => {
        if (calendarConfig === undefined) {
            return (): string | undefined => undefined;
        }
        return (row: OttopilotCustomer): string | undefined => {
            const source = calendarConfig.eventTypeSource;
            if (source === undefined) {
                return undefined;
            }
            if (source.columnId === "status" || source.columnId === "disposition") {
                return undefined;
            }
            const value = row.additionalColumnValues[source.columnId];
            if (value === undefined || value === null) {
                return undefined;
            }
            return unknownToDisplayString(value);
        };
    }, [calendarConfig]);

    const customerCalendarLegendItems = useMemo(() => {
        if (calendarConfig === undefined) {
            return [];
        }
        return resolveCalendarLegendItems(calendarConfig, calendarConfig);
    }, [calendarConfig]);

    const getCustomerCalendarTypeLabel = useMemo(() => {
        return (type: string) => {
            if (calendarConfig === undefined) {
                return type;
            }
            return resolveCalendarEventTypeLabel(type, calendarConfig, calendarConfig, type);
        };
    }, [calendarConfig]);

    const getCustomerCalendarTypeColor = useMemo(() => {
        return (type: string | undefined) => {
            if (calendarConfig === undefined) {
                return {};
            }
            return resolveCalendarEventTypeColor(type, calendarConfig, calendarConfig);
        };
    }, [calendarConfig]);

    const customerCalendarAllDayStyle = useMemo(() => {
        if (calendarConfig === undefined) {
            return {mode: "fixedGreen"} as const;
        }
        return resolveCalendarAllDayStyle(calendarConfig, calendarConfig);
    }, [calendarConfig]);

    const customersDataViewerQueryKeyPrefix = useMemo(() => {
        return [uniqueUuid`a1b2c3d4-e5f6-4789-a012-3456789abcde`, workspace.id];
    }, [workspace.id]);

    const configuredSearchColumns = customerPageConfig.ottopilotDataViewerConfig?.searchColumns ?? [];
    const resolvedSearchColumns = useMemo(() => {
        if (configuredSearchColumns.length > 0) {
            return configuredSearchColumns;
        }
        const fallbackSearchLabels = new Set([
            "customer name",
            "contact number",
            "phone number",
            "email",
            "utm_platform",
            "utm source",
            "utm_source",
            "utm medium",
            "utm_medium",
            "utm campaign",
            "utm_campaign",
            "utm term",
            "utm_term",
            "utm content",
            "utm_content",
        ]);
        return customersModuleConfiguration.additionalColumns
            .filter((column) => fallbackSearchLabels.has(normalizeLabel(column.displayName)))
            .map((column) => column.id);
    }, [configuredSearchColumns, customersModuleConfiguration.additionalColumns]);

    const searchPlaceholder =
        customerPageConfig.ottopilotDataViewerConfig?.searchPlaceholder ??
        `Search ${customersModuleConfiguration.moduleDisplayName.pluralLowerCase}`;
    const scopeFilterConfig = customerPageConfig.ottopilotDataViewerConfig?.scopeFilter;

    const ottopilotColumns = useMemo((): Array<OttopilotColumn<CustomerDataViewerRow>> => {
        if (customerPageConfig.tableViewColumns !== undefined) {
            return customerTableViewColumnsToOttopilotColumns(getAdditionalColumns, customerPageConfig.tableViewColumns, {
                users: users,
                userTemplates: userTemplates,
                btlActivities: btlActivities,
                organizationalUnits: organizationalUnits,
                organizationalUnitTemplates: organizationalUnitTemplates,
                products: products,
                formatDateTime: formatDateTime,
                eventContainerTemplate,
                eventTemplates: eventTemplates,
            });
        }
        const fromAdditional = customersModuleConfiguration.additionalColumns.map(
            (column): OttopilotColumn<CustomerDataViewerRow> => {
                const base: OttopilotColumn<CustomerDataViewerRow> = {
                    id: column.id,
                    type: OttopilotColumnType.string,
                    displayName: column.displayName,
                    valueGetter: (customer) =>
                        (customer.additionalColumnValues[column.id] as string | undefined | null) ?? "",
                    sortingCapability: true,
                    filteringCapability_contains: true,
                };
                if (
                    isEmotoradCustomersWorkspace(customersModuleConfiguration) === true &&
                    column.id === EMOTORAD__CUSTOMERS__CUSTOMER_PLATFORM_COLUMN_ID
                ) {
                    return {
                        ...base,
                        valueFormatter: (value: string) =>
                            value !== "" ? formatEmotoradCustomerPlatformCell(value) : "-",
                    };
                }
                return base;
            },
        );
        return [
            ...fromAdditional,
            {
                id: "id",
                type: OttopilotColumnType.uuid,
                displayName: "ID",
                valueGetter: (customer) => customer.id,
                sortingCapability: false,
                filteringCapability_contains: false,
            },
            {
                id: "createdAt",
                type: OttopilotColumnType.iso8601DateTime,
                displayName: "Created at",
                valueGetter: (customer) => customer.createdAt,
                valueFormatter: (value) => formatDateTime(value),
                sortingCapability: true,
                filteringCapability_contains: false,
            },
        ];
    }, [getAdditionalColumns,
        customerPageConfig.tableViewColumns,
        customersModuleConfiguration,
        users,
        userTemplates,
        btlActivities,
        organizationalUnits,
        organizationalUnitTemplates,
        products,
        eventContainerTemplate,
        eventTemplates,
    ]);

    const initialViewId =
        search.view === "list"
            ? CUSTOMERS_LIST_VIEW_ID
            : search.view === "kanban"
            ? CUSTOMERS_KANBAN_VIEW_ID
            : search.view === "calendar" && hasCalendarConfig
              ? CUSTOMERS_CALENDAR_VIEW_ID
              : CUSTOMERS_TABLE_VIEW_ID;

    const customersDataViewerControllerRef = useRef<OttopilotDataViewerController<CustomerDataViewerRow> | null>(null);
    const openCustomerProfileAside = useCallback((customer: CustomerDataViewerRow) => {
        const controller = customersDataViewerControllerRef.current;
        if (controller === null) {
            return;
        }
        controller.setCurrentlyOpenRowEngagementPanel({
            rowEngagementType: RowEngagementType.read,
            rowEngagementPanelType: RowEngagementPanelType.sidebar,
            rowEngagementData: customer,
            readPanelLayout: "entityProfile",
            hidePanelLayoutToggle: true,
        });
    }, []);

    const emotoradCustomersListPresentation = useEmotoradCustomersListCardPresentation<CustomerDataViewerRow>({
        workspaceId: workspace.id,
        navigate,
        scheduleRoute: emotoradCustomersListConfig.scheduleRoute,
        listModuleConfig: emotoradCustomersListConfig,
        customersModuleConfiguration,
        customerStatusColumnId: EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID,
        listStatusBoard: {columnConfig: resolvedKanbanConfiguration.columnConfig},
        customerPhoneColumnId: EMOTORAD__CUSTOMERS__PHONE_NUMBER_COLUMN_ID,
        openNotesPanel,
        onOpenCreateDrawer: () => {
            setCustomerListCreateDrawerOpen(true);
        },
        onDisposeRequest: (customer) => {
            if (customer.eventContainer === null) {
                return;
            }
            setCustomerLeadDispositionCustomer(customer);
        },
        onOpenProfile: openCustomerProfileAside,
    });

    const customersDataViewerController = useOttopilotDataViewerController<CustomerDataViewerRow>({
        rowDisplayNameVariants: {
            singularSentenceCase: customersModuleConfiguration.moduleDisplayName.singularSentenceCase,
            pluralSentenceCase: customersModuleConfiguration.moduleDisplayName.pluralSentenceCase,
            singularLowerCase: customersModuleConfiguration.moduleDisplayName.singularLowerCase,
            pluralLowerCase: customersModuleConfiguration.moduleDisplayName.pluralLowerCase,
        },
        queryKeyPrefix: customersDataViewerQueryKeyPrefix,
        hideEditPanelFooter: true,
        canEditRow: () => false,
        searchColumns: resolvedSearchColumns,
        searchPlaceholder: searchPlaceholder,
        scopeFilterConfig: scopeFilterConfig,
        journeyScopeFilterConfig: customersJourneyScopeFilterConfig,
        calendarDataSource: "direct",
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            const mappedFilter = tableResolvers.mapFilterConfigurationToFields(filterConfiguration);
            const mappedSort = tableResolvers.mapSortConfigurationToFields(sortConfiguration);
            const result = await getCustomers({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                workspaceId: workspace.id,
                offset: offset,
                limit: limit,
                filterConfiguration: mappedFilter,
                sortConfiguration: mappedSort,
                searchConfiguration: searchConfiguration,
            });
            if (result.success !== true) {
                throw result.error;
            }
            const rows = result.data;
            const cal = customerPageConfig.calendarConfig;
            if (cal !== undefined) {
                const titleTemplate = cal.calendarTitleOttopilotStringTemplate;
                const startColumnId = cal.calendarStartDateColumnId;
                if (titleTemplate !== undefined && startColumnId !== undefined) {
                    return rows.map((customer): CustomerDataViewerRow => {
                        const merged: Record<Uuid, unknown> = {
                            ...(customer.eventContainer !== null ? customer.eventContainer.additionalColumnValues : {}),
                            ...customer.additionalColumnValues,
                        };
                        const title = processOttopilotStringTemplate(titleTemplate, merged);
                        const fromJourney =
                            customer.eventContainer !== null
                                ? customer.eventContainer.additionalColumnValues[startColumnId]
                                : undefined;
                        const fromCustomer = customer.additionalColumnValues[startColumnId];
                        const rawStart =
                            fromJourney !== undefined && fromJourney !== null ? fromJourney : fromCustomer;
                        const startDateRaw = unknownToDisplayString(rawStart).trim();
                        const startDate = startDateRaw !== "" ? startDateRaw : customer.createdAt;
                        return {
                            ...customer,
                            title: title,
                            startDate: startDate,
                            endDate: startDate,
                            isAlldayEvent: false,
                        };
                    });
                }
            }
            return rows;
        },
        initialSortConfiguration: {
            revisionId: generateUuid(),
            lineItems: [
                {
                    columnId: "createdAt",
                    direction: SortDirection.descending,
                },
            ],
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const mappedFilter = tableResolvers.mapFilterConfigurationToFields(filterConfiguration);
            const countResult = await getCustomers_Length({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                workspaceId: workspace.id,
                filterConfiguration: mappedFilter,
                searchConfiguration: searchConfiguration,
            });
            if (countResult.success !== true) {
                throw countResult.error;
            }
            return countResult.data;
        },
        dateRangeFieldIds:
            calendarConfig?.calendarStartDateColumnId !== undefined
                ? {
                      startDateColumnId: tableResolvers.resolveTableColumnField(
                          calendarConfig.calendarStartDateColumnId,
                      ),
                      endDateColumnId: tableResolvers.resolveTableColumnField(
                          calendarConfig.calendarStartDateColumnId,
                      ),
                  }
                : undefined,
        columnValuesQueryFn: async ({columnId}) => {
            const resolved = tableResolvers.resolveTableColumnField(columnId);
            const columnValuesResult = await getCustomers_ColumnValues({
                workspaceId: workspace.id,
                columnId: resolved,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (columnValuesResult.success !== true) {
                throw columnValuesResult.error;
            }
            return columnValuesResult.data;
        },
        columns: {
            revisionId: uniqueUuid`f1e2d3c4-b5a6-4789-a012-3456789abcde`,
            lineItems: ottopilotColumns,
        },
        views: [
            ...(isEmotoradCustomersWorkspace(customersModuleConfiguration) === true && emotoradCustomersListConfig.enabled === true
                ? [
                      createOttopilotListView<CustomerDataViewerRow>(
                          CUSTOMERS_LIST_VIEW_ID,
                          emotoradCustomersListPresentation.renderListCard,
                      ),
                  ]
                : []),
            {
                id: CUSTOMERS_TABLE_VIEW_ID,
                viewType: ViewType.table,
                visibleColumns:
                    customerPageConfig.tableViewColumns !== undefined
                        ? customerPageConfig.tableViewColumns.map((c) => c.id)
                        : [
                              ...(customersModuleConfiguration.additionalColumns.map((c) => c.id) as Array<string>),
                              "id",
                              "createdAt",
                          ],
                otherArgs: {},
            },
            {
                id: CUSTOMERS_KANBAN_VIEW_ID,
                viewType: ViewType.kanbanBoard,
                filterFieldId: customersKanbanFilterFieldId,
                columnConfig: customersKanbanColumnConfigForStatus,
                cardRenderer: (customer: CustomerDataViewerRow) => (
                    <CustomerKanbanBoardCard
                        key={customer.id}
                        customer={customer}
                        kanbanBoardCardView={resolvedKanbanConfiguration.kanbanBoardCardView}
                        customersModuleConfiguration={customersModuleConfiguration}
                        eventContainerTemplate={eventContainerTemplate}
                    />
                ),
            },
            ...(hasCalendarConfig
                ? [
                      {
                          id: CUSTOMERS_CALENDAR_VIEW_ID,
                          viewType: ViewType.calendar as const,
                          getEventType: getCustomerCalendarType,
                          legendItems: customerCalendarLegendItems,
                          getEventTypeLabel: getCustomerCalendarTypeLabel,
                          getEventColor: getCustomerCalendarTypeColor,
                          allDayStyle: customerCalendarAllDayStyle,
                      },
                  ]
                : []),
        ],
        initialViewId: initialViewId,
        initialVisibleFilters: {
            revisionId: uniqueUuid`a0b1c2d3-e4f5-4678-a901-23456789abcd`,
            lineItems: (() => {
                const isEmotorad = isEmotoradCustomersWorkspace(customersModuleConfiguration);

                if (isEmotorad === true) {
                    // Emotorad Customers: show the commonly-used filters upfront (per Figma).
                    // Prefer known canonical IDs for key fields, then fall back to display-name matching.
                    const candidates: Array<{id: string; displayName: string}> = [
                        ...(customerPageConfig.tableViewColumns ?? []).map((c) => ({
                            id: c.id,
                            displayName: c.displayName,
                        })),
                        ...customersModuleConfiguration.additionalColumns.map((c) => ({
                            id: c.id,
                            displayName: c.displayName,
                        })),
                    ];

                    const byId = new Map<string, {id: string; displayName: string}>();
                    for (const c of candidates) {
                        if (byId.has(c.id) === false) {
                            byId.set(c.id, c);
                        }
                    }
                    const uniqueCandidates = [...byId.values()];

                    const pickByLabels = (labels: ReadonlyArray<string>): string | undefined => {
                        const match = uniqueCandidates.find(
                            (c) =>
                                isEmotoradCustomerAssignedDealersColumn(c) !== true
                                && matchesAnyLabel(c.displayName, labels),
                        );
                        return match?.id;
                    };

                    const desiredInOrder: Array<string | undefined> = [
                        // OttopilotCustomer Name + Contact Number are stable canonical additional columns in Emotorad.
                        uniqueCandidates.some((c) => c.id === EMOTORAD__CUSTOMERS__CUSTOMER_NAME_COLUMN_ID)
                            ? EMOTORAD__CUSTOMERS__CUSTOMER_NAME_COLUMN_ID
                            : pickByLabels(["customer name", "name"]),
                        uniqueCandidates.some((c) => c.id === EMOTORAD__CUSTOMERS__PHONE_NUMBER_COLUMN_ID)
                            ? EMOTORAD__CUSTOMERS__PHONE_NUMBER_COLUMN_ID
                            : pickByLabels(["contact number", "phone number", "mobile", "contact"]),
                        // Status is also stable, but the table can be config-driven; allow label match too.
                        uniqueCandidates.some((c) => c.id === EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID)
                            ? EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID
                            : pickByLabels(["customer status", "status"]),
                        pickByLabels(["pincode", "pin code", "customer pincode", "dealer pincode"]),
                        pickByLabels(["city", "customer city", "dealer city"]),
                        pickByLabels(["state", "customer state", "dealer state"]),
                        pickByLabels(["dealer name", "dealership"]),
                        pickByLabels(["action owner", "owner", "assigned to", "assignee"]),
                        pickByLabels(["model", "bike model", "product model"]),
                    ];

                    const resolved = Array.from(
                        new Set(
                            desiredInOrder.filter(
                                (id): id is string =>
                                    id !== undefined && id !== EMOTORAD__CUSTOMERS__ASSIGNED_DEALERS_COLUMN_ID,
                            ),
                        ),
                    );
                    if (resolved.length > 0) return resolved;
                }

                const firstTable = customerPageConfig.tableViewColumns?.[0];
                if (firstTable !== undefined) {
                    return [firstTable.id];
                }
                const first = customersModuleConfiguration.additionalColumns[0];
                if (first !== undefined) {
                    return [first.id];
                }
                return ["createdAt"];
            })(),
        },
        listCardChrome: (
            <>
                {emotoradCustomersListConfig.includeEventCreateDrawer === true ? (
                    <WorkspaceListCardCreateChrome
                        opened={customerListCreateDrawerOpen}
                        onClose={() => {
                            setCustomerListCreateDrawerOpen(false);
                            emotoradCustomersListPresentation.footerBindings.setSelectedCreateJourneyId(null);
                        }}
                        workspaceId={workspace.id}
                        items={listEventCreateDrawerItems}
                        eventContainerId={emotoradCustomersListPresentation.footerBindings.selectedCreateJourneyId}
                    />
                ) : null}
                {customerLeadDispositionCustomer !== null && customerLeadDispositionCustomer.eventContainer !== null ? (
                    <EmotoradLeadDispositionModal
                        event={{
                            id: customerLeadDispositionCustomer.eventContainer.id,
                            templateId: EmotoradEventTypes.lead as Uuid,
                            eventContainer: {
                                ...customerLeadDispositionCustomer.eventContainer,
                                events: [],
                            } as EventContainerWithEvents,
                            additionalColumnValues: {},
                            eventContainerId: customerLeadDispositionCustomer.eventContainer.id,
                            createdAt: customerLeadDispositionCustomer.eventContainer.createdAt,
                            updatedAt: customerLeadDispositionCustomer.eventContainer.updatedAt,
                            createdBy: customerLeadDispositionCustomer.eventContainer.createdBy,
                            updatedBy: customerLeadDispositionCustomer.eventContainer.updatedBy,
                            status: OttopilotEventStatus.active,
                            revisionId: customerLeadDispositionCustomer.eventContainer.revisionId,
                        }}
                        eventContainerTemplate={eventContainerTemplate}
                        queryKeyPrefix={customersDataViewerQueryKeyPrefix}
                        onClose={() => {
                            setCustomerLeadDispositionCustomer(null);
                        }}
                    />
                ) : null}
                {listNotesPanelChrome}
            </>
        ),
        readPanelComponent: (row, isExpanded) => {
            const panel = customersDataViewerControllerRef.current?.currentlyOpenRowEngagementPanel;
            if (
                panel?.rowEngagementType === RowEngagementType.read &&
                panel.rowEngagementData?.id === row.id &&
                panel.readPanelLayout === "entityProfile"
            ) {
                return (
                    <CustomerEntityProfileAside
                        customer={row}
                        customersModuleConfiguration={customersModuleConfiguration}
                        viewPanel={resolvedViewPanel}
                        profileFrom={CUSTOMER_PROFILE_FROM.customers}
                        onClose={() => {
                            customersDataViewerControllerRef.current?.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.none,
                                rowEngagementPanelType: RowEngagementPanelType.none,
                                rowEngagementData: null,
                            });
                        }}
                    />
                );
            }

            return (
                <CustomerViewPanel
                    row={row}
                    isExpanded={isExpanded}
                    viewPanel={resolvedViewPanel}
                    customersModuleConfiguration={customersModuleConfiguration}
                    eventContainerTemplate={eventContainerTemplate}
                    users={users}
                    userTemplates={userTemplates}
                    organizationalUnits={organizationalUnits}
                    organizationalUnitTemplates={organizationalUnitTemplates}
                    btlActivities={btlActivities}
                    products={products}
                    eventTemplates={eventTemplates}
                />
            );
        },
        rowEngagementDropdownOptions: (row) => {
            const exportColumns = (
                customersDataViewerController.columns.lineItems as Array<OttopilotColumn<CustomerDataViewerRow>>
            ).filter((col) => col.id !== "id");

            const dataToExport = transformRowForExport(row, exportColumns);

            return [
                <CopyDataDropdownItem
                    key="copy"
                    data={dataToExport}
                    showFlag={showFlag}
                />,
            ];
        },
    });

    const emotoradCustomersStatusNavConfig = useMemo(() => {
        if (isEmotoradCustomers !== true) {
            return null;
        }
        return getEmotoradCustomersStatusNavConfig();
    }, [isEmotoradCustomers]);

    customersDataViewerControllerRef.current = customersDataViewerController;

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: emotoradCustomersStatusNavConfig?.statusColumnId ?? null,
        setFilterConfiguration: customersDataViewerController.setFilterConfiguration,
        enabled: isEmotoradCustomers === true && emotoradCustomersStatusNavConfig !== null,
        validStatusIds: getEmotoradModuleStatusNavValidStatusIds(emotoradCustomersStatusNavConfig),
        resolveStatusFilterValues: resolveEmotoradCustomerStatusValuesForModuleNavStatusId,
    });

    const {
        onExportPdf: handleExportAllRowsAsPdf,
    } = useOttopilotExportActions({
            controller: customersDataViewerController,

            csvExportMode: {kind: "disabled"},
            pngFilename: "customers-export.png",
            pdfFilename: "customers-export.pdf",
            documentTitle: customersModuleConfiguration.moduleDisplayName.pluralSentenceCase,
            onError: (message) => {
                showFlag({
                    title: "Export failed",
                    description: message,
                    appearance: "error",
                    isAutoDismiss: true,
                    autoDismissSeconds: 3,
                });
            },
        });
    useEffect(() => {
        const viewId =
            search.view === "list"
                ? CUSTOMERS_LIST_VIEW_ID
                : search.view === "kanban"
                ? CUSTOMERS_KANBAN_VIEW_ID
                : search.view === "calendar" && hasCalendarConfig
                  ? CUSTOMERS_CALENDAR_VIEW_ID
                  : CUSTOMERS_TABLE_VIEW_ID;
        customersDataViewerController.setCurrentViewId(viewId);
    }, [search.view, hasCalendarConfig, customersDataViewerController.setCurrentViewId]);

    return (
        <>
            <TableOnlyPage
                title={"customers"}
                titleRightSection={
                    <div
                        className={
                            isMobile
                                ? "grid grid-flow-col auto-cols-max gap-[8px]"
                                : "grid grid-flow-col auto-cols-max gap-ds-space-100"
                        }
                    >
                        <AnankeDropdownMenu<HTMLButtonElement>
                            placement="bottom-start"
                            trigger={({triggerRef, ...triggerProps}) => (
                                <button
                                    {...triggerProps}
                                    ref={triggerRef as Ref<HTMLButtonElement>}
                                    type="button"
                                    className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                    style={{
                                        borderColor: token("color.border", "#091E4224"),
                                        backgroundColor: token("elevation.surface", "#FFFFFF"),
                                        color: token("color.icon", "#44546F"),
                                    }}
                                >
                                    <ShowMoreVerticalIcon label="Export options" />
                                </button>
                            )}
                        >
                            <AnankeDropdownItemGroup>
                                {/* <AnankeDropdownItem
                                    onClick={() => handleExportAllRowsAsPng()}
                                    elemBefore={<DownloadIcon label="" color={token("color.icon")} />}
                                    elemAfter={
                                        <ChevronRightIcon
                                            label=""
                                            size="small"
                                            color={token("color.icon.subtle")}
                                        />
                                    }
                                >
                                    Export as PNG
                                </AnankeDropdownItem> */}
                                <AnankeDropdownItem
                                    onClick={() => handleExportAllRowsAsPdf()}
                                    elemBefore={<DownloadIcon label="" color={token("color.icon")} />}
                                    elemAfter={
                                        <ChevronRightIcon
                                            label=""
                                            size="small"
                                            color={token("color.icon.subtle")}
                                        />
                                    }
                                >
                                    Export as PDF
                                </AnankeDropdownItem>
                            </AnankeDropdownItemGroup>
                        </AnankeDropdownMenu>
                    </div>
                }
                bottomSection={<OttopilotDataViewer controller={customersDataViewerController} />}
            />
        </>
    );
}
